/**
 * Words as tokens (design-language#words-numbers-and-anticipation).
 *
 * A domain term has three parts and only the first one is usually written
 * down: what we call it, what it means in plain language, and what we have
 * decided NOT to call it. The third is the one that keeps a legend, a help
 * popover and a page lede from drifting, because drift is almost never someone
 * inventing a new concept — it is someone reaching for a synonym.
 *
 * This module holds the SHAPE. The registries themselves are per-audience and
 * live in each tree, because the admin surface and the client surface
 * deliberately call the same states different things: an admin sees DevStride's
 * vocabulary verbatim because they sit beside the board, and a client sees
 * customer-facing wording. Two registries, one shape, one rule.
 */

export interface Term {
  /** The one word or phrase every surface uses. Title Case for a label. */
  readonly label: string;
  /**
   * One sentence, in plain language, for someone who does not work here.
   * This is what an `InlineHelp` popover renders, so it has to stand alone.
   */
  readonly gloss: string;
  /**
   * Words this term must NOT be called. Each one is a real near-miss somebody
   * would otherwise reach for, not a thesaurus dump: the list is only useful
   * if a reader believes every entry was a genuine risk.
   */
  readonly banned: readonly string[];
}

export type TermRegistry<K extends string = string> = Readonly<Record<K, Term>>;

/**
 * Identity at runtime; the work is in the types.
 *
 * It pins `label`, `gloss` and `banned` as required on every entry — so a term
 * added without a gloss fails the build rather than shipping a popover that
 * renders nothing — while keeping the key union literal for callers.
 */
export function defineTerms<K extends string>(registry: Record<K, Term>): TermRegistry<K> {
  return registry;
}

/** Every banned synonym across a registry, lower-cased, deduplicated. */
export function bannedSynonyms(registry: TermRegistry): string[] {
  return [
    ...new Set(
      Object.values(registry).flatMap((term) => term.banned.map((word) => word.toLowerCase())),
    ),
  ];
}

/**
 * Find banned synonyms in a piece of user-facing copy.
 *
 * Whole words only, case-insensitive. Returns the offenders rather than a
 * boolean, because the useful failure message names the word — "copy uses a
 * banned synonym" sends a reader hunting, "'ticket' is banned; the term is
 * 'Request'" does not.
 */
export function findBannedSynonyms(copy: string, registry: TermRegistry): string[] {
  const found: string[] = [];
  for (const word of bannedSynonyms(registry)) {
    const pattern = new RegExp(`\\b${word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
    if (pattern.test(copy)) found.push(word);
  }
  return found;
}

/**
 * The canonical label, or the raw key.
 *
 * Falling back to the key rather than to an empty string is the same choice the
 * integration-jobs table makes with its `TYPE_LABEL` fallback: an unlabelled
 * value must stay identifiable on screen, because a blank cell reads as "there
 * is nothing here" and a raw enum name reads as "somebody forgot a label".
 */
export function labelFor<K extends string>(registry: TermRegistry<K>, key: string): string {
  return (registry as TermRegistry)[key]?.label ?? key;
}
